import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import {
  CLIENT_TYPE_OPTIONS,
  CLIENT_STATUS_OPTIONS,
  CLIENT_STAGE_OPTIONS,
} from "../types/crmTypes";

const ClientForm = ({ initialData, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    firstName: "",
    lastName: "",
    displayName: "",
    email: "",
    phone: "",
    alternatePhone: "",
    whatsapp: "",
    company: "",
    type: "individual",
    status: "active",
    stage: "lead",
    source: {
      primary: "website",
      secondary: "",
      campaignId: "",
      campaignName: "",
    },
    ownership: {
      assignedTo: "",
      teamId: "",
      branchId: "",
    },
    address: {
      street: "",
      area: "",
      city: "",
      state: "",
      zipCode: "",
      country: "India",
    },
    preferences: {
      photographyStyle: [],
      eventTypes: [],
      communicationChannels: [],
      language: [],
    },
    segmentation: {
      tags: [],
      segments: [],
      priority: "medium",
    },
    notesSummary: "",
  });

  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (initialData) {
      setFormData({
        fullName: initialData.fullName || "",
        firstName: initialData.firstName || "",
        lastName: initialData.lastName || "",
        displayName: initialData.displayName || "",
        email: initialData.email || "",
        phone: initialData.phone || "",
        alternatePhone: initialData.alternatePhone || "",
        whatsapp: initialData.whatsapp || "",
        company: initialData.company || "",
        type: initialData.type || "individual",
        status: initialData.status || "active",
        stage: initialData.stage || "lead",
        source: {
          primary: initialData.source?.primary || "website",
          secondary: initialData.source?.secondary || "",
          campaignId: initialData.source?.campaignId || "",
          campaignName: initialData.source?.campaignName || "",
        },
        ownership: {
          assignedTo: initialData.ownership?.assignedTo || "",
          teamId: initialData.ownership?.teamId || "",
          branchId: initialData.ownership?.branchId || "",
        },
        address: {
          street: initialData.address?.street || "",
          area: initialData.address?.area || "",
          city: initialData.address?.city || "",
          state: initialData.address?.state || "",
          zipCode: initialData.address?.zipCode || "",
          country: initialData.address?.country || "India",
        },
        preferences: {
          photographyStyle: initialData.preferences?.photographyStyle || [],
          eventTypes: initialData.preferences?.eventTypes || [],
          communicationChannels:
            initialData.preferences?.communicationChannels || [],
          language: initialData.preferences?.language || [],
        },
        segmentation: {
          tags: initialData.segmentation?.tags || [],
          segments: initialData.segmentation?.segments || [],
          priority: initialData.segmentation?.priority || "medium",
        },
        notesSummary: initialData.notesSummary || "",
      });
    }
  }, [initialData]);

  const handleChange = (path, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      const keys = path.split(".");
      let obj = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }

      obj[keys[keys.length - 1]] = value;
      return updated;
    });

    if (errors[path]) {
      setErrors((prev) => ({ ...prev, [path]: "" }));
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (!tag) return;
    if (!formData.segmentation.tags.includes(tag)) {
      handleChange("segmentation.tags", [...formData.segmentation.tags, tag]);
    }
    setTagInput("");
  };

  const handleRemoveTag = (tag) => {
    handleChange(
      "segmentation.tags",
      formData.segmentation.tags.filter((t) => t !== tag),
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.fullName.trim() && !formData.firstName.trim()) {
      newErrors.fullName = "Client name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Divider>
          <Typography variant="caption">Basic Info</Typography>
        </Divider>

        <TextField
          label="Full Name *"
          value={formData.fullName}
          onChange={(e) => handleChange("fullName", e.target.value)}
          error={!!errors.fullName}
          helperText={errors.fullName}
          fullWidth
          disabled={loading}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
        </Grid>

        <TextField
          label="Display Name"
          value={formData.displayName}
          onChange={(e) => handleChange("displayName", e.target.value)}
          fullWidth
          disabled={loading}
          placeholder="John & Sarah"
        />

        <Divider>
          <Typography variant="caption">Contact</Typography>
        </Divider>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Alternate Phone"
              value={formData.alternatePhone}
              onChange={(e) => handleChange("alternatePhone", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="WhatsApp"
              value={formData.whatsapp}
              onChange={(e) => handleChange("whatsapp", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
        </Grid>

        <Divider>
          <Typography variant="caption">Business</Typography>
        </Divider>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company"
              value={formData.company}
              onChange={(e) => handleChange("company", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleChange("type", e.target.value)}
                label="Type"
                disabled={loading}
              >
                {CLIENT_TYPE_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleChange("status", e.target.value)}
                label="Status"
                disabled={loading}
              >
                {CLIENT_STATUS_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Stage</InputLabel>
              <Select
                value={formData.stage}
                onChange={(e) => handleChange("stage", e.target.value)}
                label="Stage"
                disabled={loading}
              >
                {CLIENT_STAGE_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider>
          <Typography variant="caption">Address</Typography>
        </Divider>

        <TextField
          label="Street"
          value={formData.address.street}
          onChange={(e) => handleChange("address.street", e.target.value)}
          fullWidth
          disabled={loading}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Area"
              value={formData.address.area}
              onChange={(e) => handleChange("address.area", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="City"
              value={formData.address.city}
              onChange={(e) => handleChange("address.city", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="State"
              value={formData.address.state}
              onChange={(e) => handleChange("address.state", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
        </Grid>

        <Divider>
          <Typography variant="caption">Tags</Typography>
        </Divider>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
          {formData.segmentation.tags.map((tag) => (
            <Chip key={tag} label={tag} onDelete={() => handleRemoveTag(tag)} />
          ))}
        </Stack>

        <Stack direction="row" spacing={1}>
          <TextField
            label="Add Tag"
            size="small"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            fullWidth
            disabled={loading}
          />
          <Button variant="outlined" onClick={handleAddTag} disabled={loading}>
            Add
          </Button>
        </Stack>

        <TextField
          label="Notes Summary"
          multiline
          rows={3}
          value={formData.notesSummary}
          onChange={(e) => handleChange("notesSummary", e.target.value)}
          fullWidth
          disabled={loading}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            {loading
              ? "Saving..."
              : initialData
                ? "Update Client"
                : "Create Client"}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default ClientForm;
